import * as React from 'react'
import Link from 'next/link'
import { NextSeo } from 'next-seo'
import styled, { css } from 'styled-components'
import * as jotaiUtils from 'jotai/utils'

import * as hooks from '@owl-nest/hooks'
import * as logger from '@owl-nest/logger'
import * as money from '@owl-nest/money'
import * as shopify from '@owl-nest/shopify'
import { useCart } from '@boutique/cart'
import * as config from '@boutique/config'
import * as entity from '@boutique/entities'
import * as core from '@boutique/services'
import * as url from '@boutique/url'
import * as utils from '@boutique/utils'
import * as cold from '@owl-nest/cold-data'

import * as plume from '@ulule/owl-kit-components/next'

import { detailLayout, Rationale, ShareGroup } from '@boutique/components'

import { Criteria } from './Criteria'
import { Options } from './Options'
import { TabInformation } from './Tabs'
import { TagDiscover } from './TagDiscover'

import { useImageSelection, useVariantSelection } from './hooks'
import type { SelectedVariant, UseProductState } from './hooks'
import type { FormattedOption } from './utils'

const OPTION_ID = 'options'

const GIFT_IDEAS_STORAGE_KEY = 'bob_gift_ideas_products'
const GIFT_IDEAS_TRACKING_KEY = 'Campagne'
const GIFT_IDEAS_TRACKING_VALUE = 'idkdo'

export type ProductPageProps = {
  product: entity.product.Product
  productId: string
  initialSelectionState: UseProductState
  formattedOptions: {
    sizeOptions: FormattedOption[]
    nonSizeOptions: FormattedOption[]
  }
}

function findCurrentHierarchy(tags: string[]): string | undefined {
  const tagsWithoutGender = tags.filter((tag) => !cold.taxonomies[cold.types['gender']].children.tags.includes(tag))
  for (const tag of tagsWithoutGender) {
    const productTypeParents = cold.tags[tag].parents.filter((parent) => cold.types['product-type'].includes(parent))
    for (const productTypeSlug of productTypeParents) {
      const productTypeTaxonomy = cold.taxonomies[productTypeSlug]
      const isMatchingAllTags = productTypeTaxonomy.children.tags.every((slug) => tags.some((tag) => tag === slug))
      if (isMatchingAllTags) {
        return productTypeTaxonomy.slug
      }
    }
  }
}

export function ProductDetail({
  initialSelectionState,
  formattedOptions,
  product: prefetchedProduct,
}: ProductPageProps): React.ReactElement<ProductPageProps> {
  const productNumericId = shopify.utils.toNumericId(prefetchedProduct.id, 'Product')

  const currentTree = cold.getTree(
    cold.hierarchy,
    // find first product-type tag whose parent taxonomy has all tags in the product tag (so a body in body-femme will match body and femme)
    findCurrentHierarchy(prefetchedProduct.realTags.map((tag) => tag.slug)),
  )

  const pageUrl = url.product({
    handle: prefetchedProduct.handle,
    id: productNumericId,
    title: prefetchedProduct.title,
    category: prefetchedProduct.category ?? undefined,
    currentTree,
  })

  jotaiUtils.useHydrateAtoms([
    [
      core.product.client.PRODUCT_CACHE,
      {
        [core.product.client.useProductId(productNumericId)]: {
          status: hooks.QueryStatus.SUCCESS,
          data: prefetchedProduct,
        },
      },
    ],
  ])

  const { response } = core.product.client.useProduct({ id: productNumericId, fetchOnMount: 'force' })

  const product = response.data

  const { selectedOptions, selectedVariant, onOptionSelect } = useVariantSelection(product, initialSelectionState)
  const { activeImageIndex, onImageSelect } = useImageSelection(product, selectedVariant, initialSelectionState)

  const [activeTabIndex, setActiveTabIndex] = React.useState<number | undefined>(undefined)
  const $tabInformation = React.useRef<HTMLDivElement>(null)

  if (!product) {
    return <></>
  }

  // FIXME: This condition should be removed when brands are no longer cold data
  let pageTitle = product.seo?.title || product.title
  pageTitle += product.brand && !('identifier' in product.brand) ? ` de ${product.brand.name}` : ''

  const strippedDescription = product.description.replace(/<[^>]*>?/gm, '')
  const shareableDescription = product.seo?.description || strippedDescription

  // This is to verify if brand is SlimColdUnmatchedBrand
  const rationale = 'identifier' in product.brand ? product.brand.rationale : ''

  const deliveryWarningDeliveryMessage = utils.shipping.getDeliveryWarningMessage(
    product.deliveryExtra,
    product.productionDays,
  )

  return (
    <>
      <NextSeo
        title={`${pageTitle} | ${product.brand.name}`}
        description={shareableDescription}
        canonical={config.SITE_URL + pageUrl.path}
        openGraph={{
          title: `${pageTitle} de ${product.brand.name}, en vente sur ${config.SITE_NAME}`,
          description: product.seo?.description || rationale || strippedDescription,
          url: config.SITE_URL + pageUrl.path,
          images: product.images.map((image) => ({
            alt: image.alt ?? undefined,
            height: image.height ?? undefined,
            url: image.src,
            width: image.width ?? undefined,
          })),
          siteName: config.SITE_NAME,
        }}
        twitter={{
          site: config.SITE_TWITTER_HANDLE,
          cardType: 'summary_large_image',
        }}
        facebook={{ appId: config.FACEBOOK_APP_ID }}
        robotsProps={{
          maxImagePreview: 'large',
        }}
      />
      <plume.Container>
        <Breadcrumbs
          crumbs={pageUrl.crumbs}
          LinkComponent={({ href, children }) => <Link href={href}>{children}</Link>}
        />
      </plume.Container>
      <Container>
        <detailLayout.GalleryColumn>
          <plume.Gallery
            focused={activeImageIndex}
            list={product.images.map((image) => ({
              alt: image.alt ?? undefined,
              height: image.height ?? undefined,
              nativeLazyLoad: true,
              src: image.src,
              srcSet: image.srcSet ?? undefined,
              width: image.width ?? undefined,
            }))}
            onItemFocus={onImageSelect}
          />
        </detailLayout.GalleryColumn>
        <detailLayout.Main>
          <TopSection>
            <h1>
              {!('identifier' in product.brand) /* This is to verify if brand is SlimColdUnmatchedBrand*/ ? (
                <plume.styles.heading.XXS asDesktop="XS">{product.brand.name}</plume.styles.heading.XXS>
              ) : (
                <plume.styles.heading.XXS asDesktop="XS">
                  <Link
                    prefetch={false}
                    href={
                      url.products({
                        query: {
                          facetFilters: {
                            brands: [product.brand.slug],
                          },
                        },
                      }).path
                    }
                  >
                    {product.brand.name}
                  </Link>
                </plume.styles.heading.XXS>
              )}
              <plume.styles.heading.L asDesktop="XL">{product.title}</plume.styles.heading.L>
            </h1>

            <Criteria criteria={[...product.criteria.environmental, ...product.criteria.social]} />

            {deliveryWarningDeliveryMessage && (
              <plume.Callout type="info">{deliveryWarningDeliveryMessage}</plume.Callout>
            )}

            <Options
              id={OPTION_ID}
              product={product}
              formattedOptions={formattedOptions}
              selectedOptions={selectedOptions}
              onOptionSelect={onOptionSelect}
            />

            {selectedVariant !== null && (
              <section>
                <StatusWrapper>
                  <plume.StatusPill color={selectedVariant?.available ? 'green' : 'red'}>
                    {getStockWording(selectedVariant)}
                  </plume.StatusPill>
                </StatusWrapper>
              </section>
            )}

            <AddToCart product={product} selectedVariant={selectedVariant} />

            <VendorMention>
              Vendu et expédié par {product.vendor.name}.{' '}
              <plume.Link onClick={() => goToShippingTab($tabInformation, setActiveTabIndex)} underlined>
                Voir plus d’informations
              </plume.Link>
            </VendorMention>
          </TopSection>

          <div ref={$tabInformation}>
            <TabInformation
              activeTabIndex={activeTabIndex}
              product={product}
              selectedVariant={selectedVariant}
              setActiveTabIndex={setActiveTabIndex}
            />
          </div>

          {!product.isGiftCard && <Rationale product={product} title="Une marque engagée !" />}
        </detailLayout.Main>
      </Container>

      <SmallContainer>
        <ShareGroup
          buttonLabel={'Partager le produit'}
          text={shareableDescription}
          title={product.title}
          url={utils.browser.IS_IN_BROWSER ? window.location.href : ''}
        >
          <ShareGroupText>Vous connaissez quelqu’un qui aimerait ce produit ? Partagez-le !</ShareGroupText>
        </ShareGroup>

        <TagDiscover product={product} />
      </SmallContainer>
    </>
  )
}

const Breadcrumbs = styled(plume.Breadcrumbs)`
  margin-bottom: 16px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 24px;
  }
`

const TopSection = styled.section`
  margin-top: 36px;

  ${plume.Callout} {
    margin-bottom: 20px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-top: 0;
    padding-left: 90px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    padding-left: 108px;
  }
`

const StatusWrapper = styled.div`
  border: 1px solid ${plume.COLORS.GREY_SHADE_5};
  border-radius: 40px;
  display: inline-flex;
  align-items: center;
`

const ShareGroupText = styled(plume.styles.copy.M)`
  font-weight: 600;
  color: ${plume.COLORS.GREY_SHADE_1};
  max-width: 300px;
  text-align: center;
  margin-bottom: 24px;
`

function goToShippingTab(
  $tabInformation: React.RefObject<HTMLDivElement>,
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number | undefined>>,
): void {
  $tabInformation?.current?.scrollIntoView()
  setActiveTabIndex(1)
  // We reset the value to undefined to allow resetting the state not just once.
  // Otherwise the value will always be 1 and will not reset the tab to the desired value.
  setTimeout(() => setActiveTabIndex(undefined), 0)
}

type AddToCartProps = {
  product: entity.product.Product
  selectedVariant?: SelectedVariant
}

function AddToCart({ product, selectedVariant }: AddToCartProps): React.ReactElement<AddToCartProps> {
  const cart = useCart()
  const router = hooks.useNextRouter()
  const isClientSide = hooks.useClientSide()

  const disabled = !(selectedVariant && selectedVariant.available)

  const [show, setShow] = React.useState<undefined | boolean>(undefined)
  const [touch, setTouch] = React.useState({})

  const timeoutCallback = React.useCallback(() => {
    setShow((show) => (show ? false : show))
    // We use touch as a dependency, to force the "change of identity" of timeoutCallback each time touche changes.
    // That way, on each touch, the useTimeout is canceled and restarted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touch])

  const variantPrice = product.hasUniquePrice
    ? money.format(product.variants[0].price.amount)
    : selectedVariant
      ? money.format(selectedVariant.price.amount)
      : undefined

  hooks.useTimeout(timeoutCallback, 5000)

  React.useEffect(() => {
    if (disabled) {
      setShow(undefined)
    } else {
      setShow(false)
    }
  }, [disabled])

  return (
    <AddToCartWrapper
      disabled={disabled}
      onClick={() => {
        if (disabled && plume.utils.isTouchDevice()) {
          setShow(true)
          setTouch({})
        }
        if (plume.utils.isMobile()) {
          router.push(url.cart().path)
        }
      }}
    >
      <AddToCartButton
        disabled={disabled}
        onClick={async () => {
          if (cart.ready && selectedVariant) {
            const lineItems: entity.checkout.PartialLineItem[] = [
              {
                quantity: 1,
                variantId: selectedVariant.id,
              },
            ]

            const giftIdeasProducts = localStorage.getItem(GIFT_IDEAS_STORAGE_KEY)
            const trackedGiftIdeasProducts: string[] | false = giftIdeasProducts && JSON.parse(giftIdeasProducts)

            if (trackedGiftIdeasProducts && trackedGiftIdeasProducts.includes(product.numericId)) {
              lineItems[0].customAttributes = [
                {
                  key: GIFT_IDEAS_TRACKING_KEY,
                  value: GIFT_IDEAS_TRACKING_VALUE,
                },
              ]
            }
            await cart.add(lineItems)
          } else {
            logger.err('[product] Cart not available', {
              cart,
              selectedVariant,
            })
          }
        }}
      >
        <plume.styles.heading.Card1 asDesktop="XS">
          Ajouter au panier
          {variantPrice ? ` - ${variantPrice}` : null}
          {product.hasUniqueCompareAtPrice && product.compareAtPriceRange?.[0] ? (
            <plume.styles.copy.M as="span">{money.format(product.compareAtPriceRange[0])}</plume.styles.copy.M>
          ) : (
            selectedVariant &&
            selectedVariant.compareAtPrice && (
              <plume.styles.copy.M as="span">{money.format(selectedVariant.compareAtPrice.amount)}</plume.styles.copy.M>
            )
          )}
        </plume.styles.heading.Card1>
      </AddToCartButton>

      <plume.Tooltip
        open={Boolean(show)}
        position={isClientSide ? (plume.utils.isMobile() ? 'top' : 'bottom') : 'bottom'}
      >
        Veuillez choisir parmi les options disponibles !
      </plume.Tooltip>
    </AddToCartWrapper>
  )
}

function getStockWording(selectedVariant: SelectedVariant): string {
  if (selectedVariant && selectedVariant.available) {
    if (selectedVariant.stock) {
      if (selectedVariant.stock === 1) {
        return 'En stock : 1 restant'
      } else if (selectedVariant.stock > 0 && selectedVariant.stock <= 10) {
        return `En stock : ${selectedVariant.stock} restants`
      }
    }
    return 'En stock'
  }

  return 'Indisponible'
}

const Container = styled(detailLayout.Container)`
  @media screen and ${plume.BREAKPOINTS.TABLET} {
    column-gap: 32px;

    ${detailLayout.Main} {
      min-width: 640px;
    }
  }
`

const AddToCartWrapper = styled.div<{ disabled: boolean }>`
  display: inline-block;
  left: 0;
  margin: 0 auto;
  position: fixed;
  right: 0;
  width: 96%;
  z-index: calc(${plume.ZINDEX.HIGH} - 2);
  border-radius: 12px;

  ${({ disabled }) => {
    if (disabled) {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          &:hover {
            ${plume.Tooltip} {
              display: flex;
            }
          }
        }
      `
    }
  }}

  @media not screen and ${plume.BREAKPOINTS.TABLET} {
    width: max-content;
    bottom: 10px;
    max-width: 70%;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    position: relative;
    margin: 24px 0 8px;
    width: initial;
    z-index: ${plume.ZINDEX.NEUTRAL};
  }
`

const AddToCartButton = styled(plume.Button)`
  align-items: center;
  display: flex;
  width: 100%;

  ${plume.styles.heading.Card1} {
    font-weight: 900;
    color: ${plume.COLORS.PRIMARY_WHITE};
  }

  ${plume.styles.copy.M} {
    text-decoration: line-through;
    color: ${plume.COLORS.PRIMARY_WHITE};
    margin-left: 16px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    white-space: nowrap;
  }
`

const SmallContainer = styled(plume.Container)`
  margin-top: 48px;
  text-align: center;

  ${ShareGroup} {
    margin: 40px auto 0;
  }
`

const VendorMention = styled(plume.styles.copy.XS)`
  @media not screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: 9px;
  }
`
